import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackLink from '../../components/BackLink';
import { ActionMode } from '../../common/enums/common.enum';
import { MESSAGE_TYPE } from '../../common/constants/common.constants';
import { FINANCE, FINANCE_INVOICES } from '../../common/constants/routes.constants';
import { InvoiceStatusEnum } from '../../common/enums/invoicing.enum';
import { InvoiceDetailsDto } from '../../common/interfaces/invoicing.interface';
import { getInvoiceDetails } from '../../api/invoicing.api';
import { createNotification } from '../../utilities/helper/notification.helper';
import { formatMonthKey, parseMonthKey } from '../../utilities/helper/date.helper';
import i18n from '../../i18n';
import CancelInvoiceModal from './components/CancelInvoiceModal';
import DraftInvoiceView from './components/Invoices/DraftInvoiceView';
import InvoiceDetailView from './components/Invoices/InvoiceDetailView';

interface InvoicePageProps {
  mode: ActionMode;
}

/**
 * One invoice: a new one, a draft, or an issued or cancelled one.
 *
 * A draft and a new invoice are the same form; an issued invoice is a document that is
 * read and paid, not edited - so the page only loads the invoice and hands it to the
 * view that fits it.
 */
const InvoicePage: React.FC<InvoicePageProps> = ({ mode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const params = new URLSearchParams(location.search);
  const kindergartenId = params.get('kindergartenId') ?? '';
  const isCreate = mode === ActionMode.CREATE;
  const queryMonth = parseMonthKey(params.get('month')) ? (params.get('month') as string) : undefined;

  const [invoice, setInvoice] = useState<InvoiceDetailsDto | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    if (!invoiceId || !kindergartenId) return;
    try {
      const result = await getInvoiceDetails(kindergartenId, invoiceId);
      setInvoice(result.results ?? null);
    } catch {
      // the i18n instance rather than the hook's t: as a dependency, t would reload on a
      // language switch and overwrite a form being edited
      createNotification(MESSAGE_TYPE.ERROR, i18n.t('invoices.page.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [invoiceId, kindergartenId]);

  useEffect(() => {
    if (!isCreate) {
      setLoading(true);
      load();
    }
  }, [isCreate, load]);

  // "← Invoices · October 2026": back to the month the invoice belongs to
  const month = invoice ? invoice.periodStart.slice(0, 7) : queryMonth;
  const listLink = `${FINANCE_INVOICES}?kindergartenId=${kindergartenId}${month ? `&month=${month}` : ''}`;
  const listLabel = month ? t('invoices.detail.backToMonth', { month: formatMonthKey(month) }) : t('invoices.title');

  // replace, and keep where "back" goes: the page navigated from is no longer worth returning to
  const openInvoice = (id: string) =>
    navigate(`${FINANCE}/invoice/${id}?kindergartenId=${kindergartenId}`, { replace: true, state: location.state });
  const leave = () => navigate((location.state as { backTo?: string } | null)?.backTo ?? listLink);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={7}>
      <BackLink fallbackTo={listLink} fallbackLabel={listLabel} />

      {isCreate || invoice?.status === InvoiceStatusEnum.DRAFT ? (
        <DraftInvoiceView
          // a new draft opened from here is a different form, not this one with new props
          key={invoice?.id ?? 'new'}
          kindergartenId={kindergartenId}
          invoice={invoice}
          initialChildId={params.get('childId') ?? undefined}
          initialMonth={queryMonth}
          onOpenInvoice={openInvoice}
          onChanged={load}
          onCancel={leave}
        />
      ) : (
        invoice && (
          <InvoiceDetailView kindergartenId={kindergartenId} invoice={invoice} onChanged={load} onCancel={() => setCancelling(true)} />
        )
      )}

      {cancelling && invoice && (
        <CancelInvoiceModal
          kindergartenId={kindergartenId}
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoiceNumber}
          onClose={() => setCancelling(false)}
          onCancelled={load}
        />
      )}
    </Box>
  );
};

export default InvoicePage;
